import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, AlertTriangle, Target, Settings, Megaphone, CheckCircle2, Quote, Users, Award, Building2, GraduationCap, Mic, Cog, BookOpen } from "lucide-react";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { HERO_IMG_FALLBACK_SRC, HERO_SIZES, HERO_SRCSET } from "@/lib/hero-lcp";

export default function Home() {
  return (
    <>
      <Navbar />

      <main id="main-content" className="flex-1 w-full relative">
        {/* 1. HERO */}
        <section className="relative min-h-[95vh] flex items-center pt-20 overflow-hidden" aria-label="Hero introduction">
          <div className="absolute inset-0 bg-background z-0" aria-hidden="true" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px] z-0" aria-hidden="true" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] -z-10 mix-blend-screen" aria-hidden="true" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-4 animate-in slide-in-from-bottom-8 duration-700 fade-in fill-mode-both relative">
              <div className="absolute -inset-x-6 -inset-y-4 bg-gradient-to-r from-background/90 via-background/70 to-transparent rounded-3xl -z-[1] lg:block hidden" aria-hidden="true" />
              <Badge variant="outline" className="w-fit border-primary/30 text-primary px-4 py-1.5 backdrop-blur-sm bg-primary/5 uppercase tracking-wider text-xs">
                Strategic Advisory
              </Badge>
              <h1 className="text-3xl sm:text-5xl md:text-7xl font-display font-bold leading-[1.1] tracking-tight text-balance">
                Turn stalled growth into <span className="text-primary italic">scalable execution.</span>
              </h1>
              <p className="text-lg sm:text-xl text-foreground/70 max-w-lg leading-relaxed">
                I advise founders and CEOs of software & SaaS companies who've hit a plateau—sharpen priorities, scale operations, and improve go-to-market execution.
              </p>
              <p className="text-sm text-primary font-medium tracking-wide">
                15 minutes. Walk away with 3–5 next moves.
              </p>
              <p className="text-xs text-muted-foreground italic">
                For software & SaaS founders with a live product.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Button size="lg" className="h-14 px-8 text-base font-semibold shadow-[0_0_40px_-10px_rgba(var(--primary),0.5)] transition-all hover:scale-105" asChild>
                  <Link href="/booking" data-testid="link-hero-cta">
                    Book a Growth Strategy Call <ArrowRight className="ml-2" size={18} aria-hidden="true" />
                  </Link>
                </Button>
                <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-primary" data-testid="link-hero-secondary">
                  See How It Works
                </a>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 mt-3 text-xs text-muted-foreground">
                <span>Mentor: <span className="text-foreground/80 font-medium">Forge McMaster</span> · <span className="text-foreground/80 font-medium">Alchemist</span> · <span className="text-foreground/80 font-medium">BuildUp</span></span>
                <span className="hidden sm:inline text-white/20">|</span>
                <span>Leadership: <span className="text-foreground/80 font-medium">RBC</span> · <span className="text-foreground/80 font-medium">TD</span> · <span className="text-foreground/80 font-medium">Sun Life</span></span>
              </div>
            </div>

            <div className="relative animate-in slide-in-from-right-12 duration-1000 fade-in fill-mode-both delay-300 h-full flex items-center justify-center">
              <div className="relative w-full max-w-[450px] aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10 opacity-60" aria-hidden="true" />
                <img
                  src={HERO_IMG_FALLBACK_SRC}
                  srcSet={HERO_SRCSET}
                  sizes={HERO_SIZES}
                  width={450}
                  height={562}
                  fetchPriority="high"
                  decoding="sync"
                  alt="Matt Rebeiro, strategic advisor for founders and CEOs"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 2. PROBLEM */}
        <section className="py-16 md:py-32 border-t border-white/5 bg-secondary/20" aria-label="The problem">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
              <span className="text-primary font-medium tracking-wide uppercase text-sm">Sound Familiar?</span>
              <div className="h-px w-8 bg-primary" aria-hidden="true" />
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-6">
              When growth stalls, the problem usually isn't effort. <br />
              <span className="text-muted-foreground">It's leverage.</span>
            </h2>
            <div className="space-y-4 text-lg text-foreground/70 max-w-2xl mx-auto mt-8">
              <p>
                You may already have a live product, a capable team, and real market traction.
              </p>
              <p>
                But growth can still plateau when operations don't scale, go-to-market underperforms, or execution loses focus.
              </p>
              <p className="text-foreground/50 text-base">
                If any of this sounds familiar, it may be time for a sharper outside perspective.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto text-left mt-8" role="list" aria-label="Common growth challenges">
              {[
                "Growth has reached a plateau",
                "Operations are not scaling with the business",
                "Product is live, but go-to-market performance is lagging",
                "Strategy exists, but execution is not producing enough traction",
              ].map((pain, i) => (
                <div key={i} role="listitem" className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-card/50">
                  <AlertTriangle size={18} className="text-primary mt-0.5 shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. WHAT I HELP FIX */}
        <section id="services" className="py-16 md:py-32 relative" aria-label="What I help fix">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">What I Help Fix</span>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold">
                Where I help Founders & CEOs <span className="text-primary">create momentum.</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8" role="list" aria-label="Service areas">
              {[
                {
                  icon: <Target size={28} />,
                  title: "Growth",
                  desc: "Clarify the highest-leverage priorities, identify the real constraints, and focus the business on what moves growth forward.",
                },
                {
                  icon: <Settings size={28} />,
                  title: "Operations",
                  desc: "Strengthen execution, improve scalability, reduce friction, and create a more effective operating rhythm.",
                },
                {
                  icon: <Megaphone size={28} />,
                  title: "Go-to-Market",
                  desc: "Improve positioning, tighten execution, and identify the bottlenecks affecting demand, conversion, and momentum.",
                },
              ].map((pillar, i) => (
                <Card key={i} role="listitem" className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-300 group overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
                  <CardContent className="p-8 relative z-10">
                    <div className="h-14 w-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" aria-hidden="true">
                      {pillar.icon}
                    </div>
                    <h3 className="text-xl font-display font-bold mb-4">{pillar.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{pillar.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 4. HOW IT WORKS */}
        <section id="how-it-works" className="py-16 md:py-32 bg-secondary/30 border-y border-white/5" aria-label="How it works">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">How It Works</span>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold">
                A focused advisory relationship <span className="text-primary">built for the next 90 days.</span>
              </h2>
            </div>

            <div className="space-y-0">
              {[
                {
                  step: "01",
                  title: "Diagnose",
                  desc: "Get clear on the biggest growth, operational, and go-to-market constraints affecting the business.",
                },
                {
                  step: "02",
                  title: "Prioritize",
                  desc: "Identify the highest-value moves and focus on what matters most instead of trying to solve everything at once.",
                },
                {
                  step: "03",
                  title: "Execute",
                  desc: "Turn strategy into action through structured accountability, sharper decision-making, and better operating cadence.",
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-6 md:gap-10">
                  <div className="flex flex-col items-center">
                    <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary font-display font-bold text-lg flex items-center justify-center shrink-0">
                      {item.step}
                    </div>
                    {i < 2 && <div className="w-px flex-1 bg-white/10 my-2" aria-hidden="true" />}
                  </div>
                  <div className="pb-12">
                    <h3 className="text-xl font-display font-bold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 border border-white/5 rounded-2xl bg-card/50 p-6 md:p-8 max-w-2xl mx-auto">
              <h3 className="text-sm font-medium text-primary uppercase tracking-wide mb-4">Support Model</h3>
              <ul className="space-y-3">
                {[
                  "Ongoing strategic advisory",
                  "Weekly or recurring CEO sessions",
                  "Practical guidance across growth, operations, and execution",
                  "90-day minimum engagement",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-foreground/80">
                    <CheckCircle2 size={14} className="text-primary shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. CREDIBILITY */}
        <section className="py-16 md:py-32 relative" aria-label="Credibility and experience">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Credibility</span>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-6">
                Experience across startups, enterprise, <span className="text-primary">and execution.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                I bring a mix of startup advisory experience, enterprise leadership exposure, and hands-on operational improvement expertise.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" role="list" aria-label="Credentials and experience">
              {[
                { icon: <Users size={22} />, label: "Startup Mentor", detail: "The Forge McMaster, Alchemist Accelerator, and BuildUp Labs" },
                { icon: <Building2 size={22} />, label: "Enterprise Leadership", detail: "RBC, TD, and Sun Life" },
                { icon: <Cog size={22} />, label: "Operational Transformation", detail: "Led automation and operational transformation at enterprise scale" },
                { icon: <Award size={22} />, label: "Lean Six Sigma", detail: "Certified Black Belt" },
                { icon: <Settings size={22} />, label: "Agile Scrum Master", detail: "Certified practitioner" },
                { icon: <GraduationCap size={22} />, label: "MBA", detail: "Entrepreneurship & Startups" },
                { icon: <BookOpen size={22} />, label: "HarvardX", detail: "Leaders in Learning" },
                { icon: <Mic size={22} />, label: "Speaker", detail: "TMU, Canadian Reinsurance Conference, SSON, Technation, and Group Futurista" },
              ].map((cred, i) => (
                <Card key={i} role="listitem" className="bg-card/50 border-white/5 p-5 hover:border-primary/20 transition-colors">
                  <div className="h-10 w-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-3" aria-hidden="true">
                    {cred.icon}
                  </div>
                  <h3 className="font-semibold text-sm mb-1">{cred.label}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{cred.detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 6. CASE STUDY / PROOF */}
        <section className="py-16 md:py-32 bg-secondary/20 border-y border-white/5" aria-label="Case study">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Case Study</span>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold">
                What this can look like <span className="text-primary">in practice.</span>
              </h2>
            </div>

            <Card className="bg-card border-white/10 p-8 md:p-12 relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-5xl md:text-6xl font-display font-bold text-primary mb-6" aria-label="10 times">10×</div>
                <h3 className="text-xl md:text-2xl font-display font-bold mb-6">
                  Feature capability in 12 months
                </h3>
                <div className="space-y-4 text-foreground/70 leading-relaxed">
                  <p>
                    At one startup, I helped improve the organization's ability to deliver product features at a dramatically higher rate over a 12-month period.
                  </p>
                  <p>
                    This involved identifying bottlenecks, improving operating effectiveness, and creating better alignment between priorities, execution, and delivery capacity.
                  </p>
                </div>
                <div className="mt-8 pt-8 border-t border-white/5">
                  <p className="text-sm font-medium text-foreground/60">Result</p>
                  <p className="text-lg font-display font-bold text-primary mt-1">A 10× increase in feature capability within 12 months.</p>
                </div>
              </div>
            </Card>

            <div className="grid md:grid-cols-3 gap-6 mt-10" role="list" aria-label="Testimonials">
              {[
                {
                  quote: "Matt's strategic mindset and ability to craft a clear roadmap for success truly stand out.",
                  name: "Rebecca",
                  title: "Global Recruitment Expert for Startups",
                  initial: "R",
                },
                {
                  quote: "Our first call was a game-changer and opened my eyes to new opportunities I hadn't considered.",
                  name: "Bogomil",
                  title: "Startup Founder",
                  initial: "B",
                },
                {
                  quote: "What truly sets Matt apart is his uncanny ability to stay ahead of the curve with emerging technologies and leverage them with outside-the-box thinking.",
                  name: "Nick",
                  title: "Startup Product Leader",
                  initial: "N",
                },
              ].map((t, i) => (
                <Card key={i} role="listitem" className="bg-card border-white/10 p-6 md:p-8 relative overflow-hidden flex flex-col">
                  <div className="absolute top-4 right-5 text-primary/10" aria-hidden="true">
                    <Quote size={40} />
                  </div>
                  <p className="text-sm md:text-base text-foreground/80 leading-relaxed italic mb-auto pb-6 relative z-10">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3 relative z-10">
                    <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-display font-bold text-xs shrink-0" aria-hidden="true">
                      {t.initial}
                    </div>
                    <div>
                      <p className="font-semibold text-sm" data-testid={`text-testimonial-name-${i}`}>{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.title}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 7. BEST FIT */}
        <section className="py-16 md:py-32 relative" aria-label="Best fit criteria">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Who This Is Best For</span>
                <div className="h-px w-8 bg-primary" aria-hidden="true" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-6">
                Best fit for Founders & CEOs who need <span className="text-primary">sharper strategy and stronger execution.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                This advisory model is best suited to leaders who:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" role="list" aria-label="Best fit criteria">
              {[
                "Have a product in market",
                "Are generating revenue",
                "Want to break through a growth plateau",
                "Need stronger execution across growth, operations, or go-to-market",
                "Value strategic thinking paired with practical action",
              ].map((item, i) => (
                <div key={i} role="listitem" className={`flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-card/50 hover:border-primary/20 transition-colors ${i === 4 ? "sm:col-span-2 sm:max-w-md sm:mx-auto" : ""}`}>
                  <CheckCircle2 size={18} className="text-primary shrink-0" aria-hidden="true" />
                  <p className="text-sm text-foreground/80">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. FINAL CTA */}
        <section className="py-16 md:py-32 relative overflow-hidden border-t border-white/5" aria-label="Final call to action">
          <div className="absolute inset-0 bg-primary/5" aria-hidden="true" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-primary/20 blur-[100px] rounded-full -z-10" aria-hidden="true" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <h2 className="text-3xl sm:text-5xl md:text-6xl font-display font-bold mb-8">
              Book a Growth Strategy Call
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Use this focused 15-minute call to pressure-test what may be slowing growth and identify the next moves worth prioritizing.
            </p>
            <Button size="lg" className="h-14 px-10 text-base font-semibold shadow-[0_0_40px_-10px_rgba(var(--primary),0.5)] transition-all hover:scale-105" asChild>
              <Link href="/booking" data-testid="link-final-cta">
                Book a Growth Strategy Call <ArrowRight className="ml-2" size={18} aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
